import {
  getPlatformURL,
  useIsInternalDomain,
} from '@stripe-internal/connect-embedded-lib';
import * as React from 'react';
import {useFlag} from '@sail/access';
import type {
  ConnectElementConfigEnabledKey,
  ConnectElementImportKeys,
  ReleaseState,
} from './ConnectJSInterface/ConnectElementList';
import {getTestmodeLogger} from '../utils/getLogger';
import {DevNotice} from '../components/DevNotice';
import {useAuthContext} from '../data-layer-client/AuthContext/AuthContext';
import {ComponentOverrideFlag} from '../utils/flags/flags';
import type {HostApp} from './ConnectJSInterface/InitAndUpdateOptionsTypes';
import {mapConnectElementToAccountEmbeddedSurfaceEnum} from '../data-layer-client/utils';
import useUpdateEmbeddedSurfacesLastAccessedMutation from '../data-layer-client/useUpdateEmbeddedSurfacesLastAccessedMutation';

const ACCOUNT_SESSION_CREATE_API_DOC =
  'https://stripe.com/docs/api/account_sessions/create';
const EMBEDDED_COMPONENTS_DOC =
  'https://stripe.com/docs/connect/get-started-connect-embedded-components#supported-connect-embedded-components';

type Props = {
  connectElement: ConnectElementImportKeys;
  enabledKey: ConnectElementConfigEnabledKey;
  releaseState: ReleaseState;
  skipEnabledCheck: boolean;
  hostApp: HostApp;
};

export const useConnectEmbeddedComponentEnabled = (
  enabledKey: ConnectElementConfigEnabledKey,
  releaseState?: ReleaseState,
): boolean => {
  const {authInfo} = useAuthContext();
  const isAllowedStripeDomain = useIsInternalDomain();
  const enabledOverrideFlag = useFlag(
    enabledKey === 'internal' ? 'false' : ComponentOverrideFlag[enabledKey],
  );

  if (releaseState === 'internal') {
    // If an internal component has no enabledKey configured and and we are in an internal domain, we always render
    if (enabledKey === 'internal' && isAllowedStripeDomain) {
      return true;
    }

    // If the component is internal and running outside of a stripe domain, we don't render
    if (!isAllowedStripeDomain) {
      return false;
    }
  }

  // If loading, we assume the component is on
  if (!authInfo) {
    return true;
  }

  // If the flag is enabled (regardless of auth completion)
  if (enabledOverrideFlag) {
    return true;
  }

  // If the enabled key is internal, it cannot be turned on by account session parameters
  if (enabledKey === 'internal') {
    return false;
  }

  // Return the enabled value
  return !!authInfo?.components[enabledKey].enabled;
};

export const ConnectElementEnabledCheck: React.FC<Props> = ({
  children,
  connectElement,
  enabledKey,
  releaseState,
  skipEnabledCheck,
  hostApp,
}) => {
  const {authInfo} = useAuthContext();
  const isConnectElementEnabled =
    useConnectEmbeddedComponentEnabled(enabledKey, releaseState) ||
    skipEnabledCheck;

  React.useEffect(() => {
    // We have to check for authInfo as well otherwise this warning will be logged
    // even when the embedded component is rendered.
    // This is because the flags are only instantiated after the auth context is set.
    if (authInfo && !isConnectElementEnabled) {
      const testmodeLogger = getTestmodeLogger(!authInfo.livemode);

      const action = `To enable this component, specify the ${enabledKey} component as enabled in the account session create request <${ACCOUNT_SESSION_CREATE_API_DOC}>. See the documentation around the ${enabledKey} component here: <${EMBEDDED_COMPONENTS_DOC}>.`;
      if (releaseState === 'internal') {
        testmodeLogger.warn(
          'You are trying to use an internal component in an external domain, so the component will be disabled.',
        );
      } else {
        testmodeLogger.warn(
          `Warning: The ${connectElement} component is not rendered as it is not enabled for your Stripe account. ${action}`,
        );
      }
    }
  }, [
    authInfo,
    isConnectElementEnabled,
    connectElement,
    enabledKey,
    releaseState,
  ]);

  const surface = mapConnectElementToAccountEmbeddedSurfaceEnum(connectElement);

  const [updateLastAccessed] = useUpdateEmbeddedSurfacesLastAccessedMutation({
    onError: () => {}, // This is so that there is no error thrown when testing for an error - https://github.com/apollographql/apollo-client/issues/7167
  });
  const hasLogged = React.useRef(false);

  React.useEffect(() => {
    // If the hostApp isn't a platform, we shouldn't log the embedded surface
    if (hostApp !== 'platform') {
      return;
    }

    // If a component does not have a valid enabledKey, we don't log
    if (enabledKey === 'internal') {
      return;
    }

    // If we're in an auth loading state, don't log
    if (!authInfo) {
      return;
    }

    // If we are in a disabled state for the component, don't log
    if (!isConnectElementEnabled) {
      return;
    }

    // Avoid any possible duplicate logging
    if (hasLogged.current) {
      return;
    }
    hasLogged.current = true;

    updateLastAccessed({
      variables: {
        input: {
          surface,
          url: getPlatformURL(),
        },
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!authInfo]);

  if (!isConnectElementEnabled)
    return (
      <DevNotice type="componentNotEnabled" connectElement={connectElement} />
    );

  return <>{children}</>;
};
